import React, { useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import DeleteConfirmModal from "components/ui/DeleteConfirmModal";
import { tasksApi } from "api/tasks";
import { getCurrentUser } from "api/client";
import { useToast } from "components/ui/Toast";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const TYPE_LABELS = {
  follow_up: "Follow up",
  call: "Call",
  meeting: "Meeting",
  import_check: "Import check",
};

const STATUS_LABELS = {
  open: { label: "Open", bg: "#DBEAFE", color: "#1D4ED8" },
  done: { label: "Done", bg: "#DCFCE7", color: "#15803D" },
  cancelled: { label: "Cancelled", bg: "#FEE2E2", color: "#B91C1C" },
};

/**
 * Tasks (Part 72 §72.3): list + complete + create. Follow-up tasks created
 * from approved AI next-step suggestions (Sprint 3) appear here, linked to
 * their conversation.
 */
export default function Tasks() {
  const currentUser = getCurrentUser();
  const isManager = currentUser?.role === 'Owner' || currentUser?.role === 'Manager';
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("follow_up");
  const [dueDate, setDueDate] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);

  const load = async () => {
    try {
      const items = await tasksApi.getAll("", { status: statusFilter });
      setTasks(items);
      setLoadError("");
    } catch (error) {
      setLoadError(error?.message || "Unable to load tasks.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const handleCreate = async () => {
    if (!title.trim() || isCreating) return;
    setIsCreating(true);
    try {
      await tasksApi.create({
        title: title.trim(),
        type,
        dueUtc: dueDate ? new Date(dueDate).toISOString() : undefined,
      });
      toast.success("Task created.");
      setTitle("");
      setDueDate("");
      await load();
    } catch (error) {
      setLoadError(error?.message || "Could not create the task.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleComplete = async (task) => {
    try {
      await tasksApi.update(task.id, { status: task.status === "done" ? "open" : "done" });
      await load();
    } catch (error) {
      setLoadError(error?.message || "Could not update the task.");
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;
    try {
      await tasksApi.remove(deleteTaskId);
      toast.success("Task deleted.");
      setIsDeleteOpen(false);
      setDeleteTaskId(null);
      await load();
    } catch (error) {
      toast.error(error?.message || "Could not delete the task.");
    }
  };

  const overdue = (task) => task.status !== "done" && task.dueUtc && new Date(task.dueUtc) < new Date();

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
              Tasks
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              Follow-ups from AI-approved next steps land here automatically
            </p>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border font-caption"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", height: 38 }}
            aria-label="Filter tasks by status"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
            <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} onClick={load}>
              Retry
            </Button>
          </div>
        )}

        {/* Create */}
        <div className="mb-5 rounded-xl border p-4 flex flex-col sm:flex-row gap-3" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            placeholder="New task title..."
            className="flex-1 rounded-lg border px-3 py-2 font-caption text-sm"
            style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)", outline: "none" }}
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="rounded-lg border px-3 py-2 font-caption text-sm"
            style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
          >
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="rounded-lg border px-3 py-2 font-caption text-sm"
            style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
          />
          <Button variant="default" size="sm" iconName="Plus" iconPosition="left" iconSize={14} onClick={handleCreate} loading={isCreating}>
            Add Task
          </Button>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <Icon name="CheckSquare" size={20} color="var(--color-text-secondary)" className="mx-auto mb-2" />
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>No tasks here yet.</p>
          </div>
        ) : (
          <div className="rounded-xl border divide-y overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            {tasks.map((task) => {
              const status = STATUS_LABELS[task.status] || STATUS_LABELS.open;
              const isOverdue = overdue(task);
              return (
                <div key={task.id} className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => handleComplete(task)}
                    className="flex items-center justify-center w-5 h-5 rounded-full border flex-shrink-0"
                    style={{
                      borderColor: task.status === "done" ? "var(--color-success)" : "var(--color-border)",
                      background: task.status === "done" ? "var(--color-success)" : "transparent",
                    }}
                    aria-label={task.status === "done" ? "Reopen task" : "Complete task"}
                  >
                    {task.status === "done" && <Icon name="Check" size={12} color="#fff" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="font-caption text-sm truncate" style={{ color: task.status === "done" ? "var(--color-text-secondary)" : "var(--color-foreground)", textDecoration: task.status === "done" ? "line-through" : "none" }}>
                      {task.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-caption text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: status.bg, color: status.color }}>
                        {status.label}
                      </span>
                      <span className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                        {TYPE_LABELS[task.type] || task.type}
                      </span>
                      {task.linkedType && (
                        <span className="flex items-center gap-1 font-caption text-[11px]" style={{ color: "var(--color-primary)" }}>
                          <Icon name="MessageSquare" size={10} color="currentColor" />
                          from {task.linkedType}
                        </span>
                      )}
                      {isOverdue && (
                        <span className="font-caption text-[11px] font-semibold" style={{ color: "#DC2626" }}>Overdue</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                      {task.dueUtc ? `Due ${formatDate(task.dueUtc)}` : formatDate(task.createdAtUtc)}
                    </span>
                    {isManager && (
                      <button
                        onClick={() => {
                          setDeleteTaskId(task.id);
                          setIsDeleteOpen(true);
                        }}
                        className="flex items-center justify-center rounded-md transition-colors hover:bg-red-50"
                        style={{ width: 26, height: 26, color: "#DC2626" }}
                        title="Delete task"
                        aria-label="Delete task"
                      >
                        <Icon name="Trash2" size={13} color="currentColor" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DeleteConfirmModal
          isOpen={isDeleteOpen}
          itemName="Task"
          onClose={() => {
            setIsDeleteOpen(false);
            setDeleteTaskId(null);
          }}
          onConfirm={handleDeleteTask}
        />

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t" style={{ borderColor: "var(--color-border)" }}>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            &copy; {new Date().getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            AI-approved next steps appear as follow-up tasks (Sprint 3)
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
